import Divider from "@ui-kit/data-display/Divider/Divider.web";
import Modal from "@ui-kit/overlays/Modal/Modal.web";
import { NODE_ENV } from "@shared/env";
import { useUserSessionAuthState } from "@shared/modules/auth/contexts/useUserSessionAuthState";
import { isPlatform } from "@shared/utils/get-platform";
import { formatRelativeStaticPath } from "@shared/utils/images/utils";
import { useTranslation } from "@shared/utils/internationalization";
import url from "@shared/utils/urls/url";
import Button from "@ui-kit/form/Button/Button.web";
import Box from "@ui-kit/layout/Box/Box.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import CDNImage from "@ui-kit/media/CDNImage/CDNImage.web";
import Text from "@ui-kit/typography/Text/Text.web";
import Cookies from "js-cookie";
import Link from "@ui-kit/navigation/Link/Link.web";
import { useRouter } from "next/router";
import { Fragment } from "react";
import { NavBarMobileModalProps } from "./NavBarMobileModal.props";

const textButtonProps = {
  p: "none",
  pl: "30px",
  width: "100%",
  height: "54px",
  justifyContent: "flex-start",
  fontSize: "1rem",
} as const;

const NavBarMobileModal = ({
  onClose,
  isOpen,
  loggedInLinks,
  loggedOutLinks,
  locales,
  goToLocale,
  logInOnClick,
  signUpOnClick,
  handleLogout,
}: NavBarMobileModalProps) => {
  const router = useRouter();
  const { t } = useTranslation("common");
  const { isLoggedIn, userProfileData: user } = useUserSessionAuthState();

  const callAndHandleClose = (callback: () => void) => {
    if (callback) {
      callback();
    }
    onClose();
  };

  return (
    <Modal onClose={onClose} size="full" isOpen={isOpen}>
      <Modal.Body
        pt="5xl"
        pb="4xl"
        px="none"
        display="flex"
        flexDirection="column"
      >
        <Modal.CloseButton color="font.dark" />
        {isLoggedIn ? (
          <Box flexGrow={1} mb="2xl">
            {user && (
              <>
                <Flex alignItems="center" ml="xl" mb="2xl">
                  <>
                    <Box
                      display="inline-flex"
                      userSelect="none"
                      borderRadius="4xl"
                      border="2px solid"
                      borderColor="red.500"
                      overflow="hidden"
                    >
                      <CDNImage
                        width={50}
                        height={50}
                        alt={t("User profile image")}
                        src={
                          user.profile_picture ||
                          formatRelativeStaticPath("images/no-avatar.jpg")
                        }
                      />
                    </Box>
                    <Box width="element.xs" />
                    <Text fontSize="xl" data-sentry-mask>
                      {user.username}
                    </Text>
                  </>
                </Flex>
                {!isPlatform("desktop") &&
                  Boolean(Cookies.get("showpass-venue-id")) && (
                    <Link href={url("dashboard")} asChild>
                      <Button.Nav
                        as="span"
                        {...textButtonProps}
                        data-testid="navbar-mobile-modal-dashboard"
                        onClick={onClose}
                      >
                        {t("Dashboard")}
                      </Button.Nav>
                    </Link>
                  )}
                {/** this button is for development/testing convenience; button links straight to the new manage dashboard */}
                {Boolean(Cookies.get("showpass-venue-id")) &&
                  (NODE_ENV === "local" || NODE_ENV === "preview") && (
                    <Link href={url("manage")} asChild>
                      <Button.Nav
                        as="span"
                        {...textButtonProps}
                        data-testid="navbar-mobile-modal-manage"
                        onClick={onClose}
                      >
                        {t("Manage")}
                      </Button.Nav>
                    </Link>
                  )}
              </>
            )}
            <Box h="element.2xs" />
            {loggedInLinks.map((link) => {
              return (
                <Fragment key={link.href}>
                  <Link href={link.href} asChild>
                    <Button.Nav
                      as="span"
                      {...textButtonProps}
                      onClick={onClose}
                      data-testid={`navbar-mobile-modal-${link.label}-button`}
                    >
                      {link.label}
                    </Button.Nav>
                  </Link>
                  <Box h="element.2xs" />
                </Fragment>
              );
            })}
            <Button.Nav
              {...textButtonProps}
              data-testid="navbar-mobile-modal-logout"
              onClick={() => callAndHandleClose(handleLogout)}
              color="secondary.600"
            >
              {t("Log Out")}
            </Button.Nav>
          </Box>
        ) : (
          <Box flexGrow={1} mb="2xl">
            <Button
              data-testid="navbar-mobile-modal-login-button"
              size="lg"
              variant="outline"
              colorPalette="primary"
              onClick={() => callAndHandleClose(logInOnClick)}
              pl="5xl"
              pr="5xl"
              ml="2xl"
              mb="2xl"
            >
              {t("Log In")}
            </Button>
            {!isPlatform("desktop") && (
              <Button.Nav
                {...textButtonProps}
                data-testid="navbar-mobile-modal-create-account-button"
                onClick={signUpOnClick}
              >
                {t("Create Account")}
              </Button.Nav>
            )}
            <Box pl={30} pr={30}>
              <Divider borderColor="gray.400" my="lg" mx="none" opacity={1} />
            </Box>
            {loggedOutLinks?.map((link, index) => {
              return (
                <Box
                  key={link.label}
                  mb={
                    index < (loggedOutLinks?.length ?? 0) - 1
                      ? "2xs"
                      : undefined
                  }
                >
                  <Link href={link.href} asChild>
                    <Button.Nav
                      as="span"
                      {...textButtonProps}
                      onClick={onClose}
                      data-testid={`navbar-mobile-modal-${link.label}-button`}
                    >
                      {link.label}
                    </Button.Nav>
                  </Link>
                </Box>
              );
            })}
          </Box>
        )}
      </Modal.Body>

      <Modal.Footer>
        {Object.keys(locales).map((locale, index) => {
          const { label: optionLabel } = locales[locale];
          const localeKeys = Object.keys(locales);
          return (
            <Flex
              key={locale}
              onClick={() => goToLocale(locale)}
              mb={index < localeKeys.length - 1 ? "2xs" : undefined}
            >
              <Button.Nav
                data-testid={`navbar-mobile-modal-${locale}-locale-button`}
                pl={30}
                width="100%"
                justifyContent="flex-start"
                height="element.4xl"
                color={router.locale === locale ? "font.dark" : "gray.500"}
                _focus={{
                  backgroundColor: "gray.300",
                }}
                _hover={{
                  backgroundColor: "gray.300",
                  color: router.locale === locale ? "font.dark" : "gray.500",
                }}
              >
                {optionLabel}
              </Button.Nav>
            </Flex>
          );
        })}
      </Modal.Footer>
    </Modal>
  );
};
export default NavBarMobileModal;
